import { PopupContext } from './popup-context';
import usePopup from './use-popup';

import type { ReactElement } from 'react';

import type { PopupProps } from './popup.types';

import './popup.scss';

/**
 * The `Popup` component is a container that manages the display and behavior of a pop-up interface.
 * It wraps around the `PopupTrigger` and `PopupContent` components, handling the logic for
 * showing and hiding the pop-up based on user interactions or specified properties.
 *
 * ### Usage
 *
 * ```tsx
 * import { Popup, PopupTrigger, PopupContent PopupClose } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Popup>
 *       <PopupTrigger>Trigger</PopupTrigger>
 *       <PopupContent>Content</PopupContent>
 *       <PopupClose>Close</PopupClose>
 *     </Popup>
 *   );
 * }
 * ```
 */

const Popup = ({ children, ...props }: PopupProps): ReactElement => {
  const popupContextValue = usePopup(props);

  return <PopupContext.Provider value={popupContextValue}>{children}</PopupContext.Provider>;
};

Popup.displayName = 'Popup';

export default Popup;
